import { ThemeRegistry } from './ThemeRegistry.js';
import { DialogComponent } from '../components/DialogComponent/DialogComponent.js';
import { mountWidgets } from './WidgetsMount.js';
import { Validation } from './Validation.js';
import { Loc } from '../locales/registry.js';
import { PageCloseController } from './PageCloseController.js';
import { wireNeighborLabels } from './tools/NeighborLabelWiring.js';
import { revealContacts } from './tools/ContactRevealer.js';
import { acceptCookies } from './tools/AcceptCookies.js';
import { touchHover } from './tools/TouchHover.js';
import { initTextarea } from './tools/InitTextarea.js';
import { modalAside } from './tools/ModalAside.js';
import { checkHelpBlock } from './tools/checkHelpBlock.js';

/**
 * Ядро системы ImSys.
 * Инициализация и доступ к конфигурации.
 */
class ImSys {
    /**
     * Инициализация системы. Вызывается один раз.
     * @param config Настройки инициализации
     */
    static init(config) {
        if (this.core) {
            throw new Error('ImSys is already initialized');
        }
        const dataset = document.body.dataset;
        // 🧩 Неизменяемая часть (API)
        const apiConfig = Object.freeze({
            siteKey: dataset.site,
            siteBaseKey: dataset.sitebase,
            pageKey: dataset.page,
            cfgVersion: dataset.version,
            cmdHost: '/imsys/',
            debugModuleBasePath: dataset.debugmodulebase || undefined
        });
        // ⚙️ Изменяемая часть (UI/UX)
        const runtimeConfig = {
            lang: dataset.lang ?? 'en',
            uiLang: dataset.uiLang || undefined,
            debug: false,
            theme: dataset.theme ?? 'default',
            legacyJqView: true, // Пока поддержка по умолчанию включена
            ...config
        };
        this.core = new ImSys(apiConfig, {
            ...runtimeConfig,
            uiLang: runtimeConfig.uiLang || (runtimeConfig.lang ?? 'en')
        });
        // Подключаем контроль закрытия страницы
        PageCloseController.install();
    }
    /**
    * Вызывает initDom для указанного корня.
    */
    static initDom(root) {
        if (!(root instanceof HTMLElement)) {
            // Защита: иногда приходят текстовые или служебные узлы
            console.error("[ImSys] initDom: root is not HTMLElement", root);
            return;
        }
        // Разметочный проход (примочки к DOM до инициализации виджетов)
        wireNeighborLabels(root, {
            nextId: () => `_id${ImSys.tmp_id++}`,
        });
        // Раскодирование контактов
        revealContacts(root);
        // Подтверждение куки
        acceptCookies(root);
        // TODO проверить если отключить (для мобилы, видео), 
        // если нужно то по хорошему перенести там где видео(в ImSys не должно быть)
        touchHover(root);
        // Возможно надо переделать на dialog (перенес из imview)
        modalAside(root);
        // Подключение autoresize для полей textarea и символ-счётчика
        initTextarea(root);
        // Инициализируем виджеты
        mountWidgets(root);
        // Инициализируем валидацию
        Validation.init(root);
        // Инициализация помощи
        checkHelpBlock(root);
        document.body.dispatchEvent(new CustomEvent('onDomInited', {
            bubbles: true,
            detail: { root }
        }));
    }
    /**
    * Вызывает initDom для указанного корня.
    */
    static initDomMany(nodes) {
        for (const el of nodes) {
            this.initDom(el);
        }
    }
    /** Ключ сайта */
    static get siteKey() {
        return ImSys.get().apiConfig.siteKey ?? 'unknown';
    }
    /** Ключ сайта */
    static get siteBaseKey() {
        return ImSys.get().apiConfig.siteBaseKey ?? 'unknown';
    }
    /** Ключ страницы */
    static get pageKey() {
        return ImSys.get().apiConfig.pageKey ?? 'unknown';
    }
    /** Базовый каталог debug-бандла ES-модулей (undefined, если не задан) */
    static get debugModuleBasePath() {
        return ImSys.get().apiConfig.debugModuleBasePath;
    }
    /** Командный хост */
    static get cmdHost() {
        return ImSys.get().apiConfig.cmdHost;
    }
    /** Язык данных и существующего transport lang. */
    static get lang() {
        return ImSys.get().config.lang ?? 'en';
    }
    /** Язык интерфейса, выбранный при инициализации страницы. */
    static get uiLang() {
        return ImSys.get().config.uiLang;
    }
    /** Флаг совместимости jQuery */
    static get legacyJqView() {
        return this.get().config.legacyJqView ?? true;
    }
    /** Флаг режима дебаг */
    static get debug() {
        return this.core?.config.debug;
    }
    /**
     * Статический доступ к реестру тем.
     */
    static get themes() {
        return this.core._themes;
    }
    /**
     * Возвращает текущий экземпляр системы.
     */
    static get() {
        if (!this.core) {
            throw new Error('ImSys is not initialized');
        }
        return this.core;
    }
    constructor(api, config) {
        this.apiConfig = api;
        this.config = config;
        this._themes = new ThemeRegistry(config.theme); // ← инициализация реестра тем
    }
    /**
     * Возвращает защищённую системную конфигурацию.
     */
    getApiConfig() {
        return this.apiConfig;
    }
    //Warning! Предлагаю поправить вызовы, которые идут с onConfirm на вызов alertConfirm
    //formCss по сути не нужен
    static alert(title, text, onConfirm, icon, formCss) {
        let alertType;
        switch (icon) {
            case 'input':
                alertType = 'info';
                break;
            case 'error':
                alertType = 'error';
                break;
            case 'message':
                alertType = 'success';
                break;
            case 'alert':
            default:
                alertType = 'warning';
                break;
        }
        if (title === text)
            text = undefined;
        DialogComponent.show({
            title: title,
            content: text,
            //Здесь сделал из-за notallowed в imview, по идее нужен другой подход
            class: formCss,
            dialogClass: formCss,
            alertType: alertType,
            alert: true,
            buttons: [
                {
                    onClick(dialog) {
                        if (onConfirm && typeof onConfirm === "function")
                            onConfirm(dialog);
                    },
                    class: 'btp1',
                    text: Loc.close
                }
            ]
        });
    }
    //От icon здесь по сути избавиться
    static alertConfirm(title, text, onConfirm, onCancel, icon, modal, confirmText) {
        let css = 'question';
        if (title == text)
            text = '';
        // 2026-09-14: возвращаем диалог, чтобы вызывающий код мог отследить закрытие без кнопок
        return DialogComponent.show({
            title: title,
            content: text,
            dialogClass: css,
            modal,
            alert: true,
            buttons: [
                {
                    onClick(dialog) {
                        if (onCancel && typeof onCancel === "function")
                            onCancel(dialog);
                    },
                    class: 'btp2',
                    text: Loc.cancel
                },
                {
                    onClick(dialog) {
                        if (onConfirm && typeof onConfirm === "function")
                            onConfirm(dialog);
                    },
                    class: 'btp1',
                    text: (confirmText == undefined ? Loc.confirm : confirmText)
                }
            ]
        });
    }
    static modalProgress(progress, onCancel) {
        DialogComponent.show({
            title: Loc.progress,
            content: progress,
            draggable: true,
            dialogClass: 'progress',
            buttons: [
                {
                    onClick(dialog) {
                        if (onCancel != undefined && typeof (onCancel) === "function")
                            onCancel(dialog);
                    },
                    class: 'btp2',
                    text: Loc.cancel,
                    autoClose: false
                },
                {
                    onClick(dialog) {
                    },
                    class: 'btp1',
                    text: Loc.close
                }
            ]
        });
    }
}
/** Счетчик для условной уникальности элементов */
ImSys.tmp_id = 0;
window.ImSys = ImSys;

export { ImSys };
